import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Alert,
} from '@mui/material';
import { CheckCircle, Dashboard, Payment } from '@mui/icons-material';
import { useNotification } from '../hooks/useNotification';
import { paymentsApi } from '../services/api';

/**
 * Payment Success Page
 * Shown after successful payment
 */
const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess } = useNotification();
  const [config, setConfig] = useState({ pagesPerMonth: 100 });

  useEffect(() => {
    // Show success notification
    showSuccess('Payment successful! Your subscription is now active.');

    // Load config
    loadConfig();

    // Log session ID if available
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      console.log('Payment session ID:', sessionId);
    }
  }, [searchParams, showSuccess]);

  const loadConfig = async () => {
    try {
      const response = await paymentsApi.getConfig();
      setConfig({
        pagesPerMonth: response.data.pagesPerMonth || 100
      });
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Stack spacing={3} alignItems="center" textAlign="center">
            {/* Success Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
            </Box>

            {/* Title */}
            <Typography variant="h4" fontWeight="bold" color="success.main">
              Payment Successful!
            </Typography>

            {/* Message */}
            <Typography variant="body1" color="text.secondary">
              Thank you for subscribing to PinePocket Pro. Your account has been
              upgraded and you now have access to all features.
            </Typography>

            {/* Details */}
            <Alert severity="success" sx={{ width: '100%' }}>
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight="medium">
                  Your subscription is now active
                </Typography>
                <Typography variant="body2">
                  • {config.pagesPerMonth} pages per month
                  <br />
                  • AI-powered categorization
                  <br />
                  • Advanced analytics
                  <br />• Monthly automatic renewal
                </Typography>
              </Stack>
            </Alert>

            {/* Action Buttons */}
            <Stack spacing={2} width="100%" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Dashboard />}
                onClick={() => navigate('/dashboard')}
                fullWidth
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Payment />}
                onClick={() => navigate('/subscription')}
                fullWidth
              >
                View Subscription Details
              </Button>
            </Stack>

            {/* Footer */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              You will receive a confirmation email shortly with your payment
              details.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PaymentSuccess;

